import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { chatApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, MessageCircle, Plus, Send, Search, Users, User, Circle } from 'lucide-react';

export default function ChatPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [typing, setTyping] = useState(null);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    chatApi.getConversations().then(d => { setConversations(d.conversations || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  // WebSocket connection
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/chat?token=${token}`);
    wsRef.current = ws;
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'new_message') {
        if (data.conversation_id === activeConv?.conversation_id) {
          setMessages(prev => [...prev, data.message]);
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        }
        setConversations(prev => prev.map(c => c.conversation_id === data.conversation_id ? { ...c, last_message: data.message.content, last_message_at: data.message.created_at, unread_count: c.conversation_id === activeConv?.conversation_id ? 0 : (c.unread_count || 0) + 1 } : c));
      } else if (data.type === 'typing') {
        setTyping(data);
        setTimeout(() => setTyping(null), 3000);
      }
    };
    ws.onclose = () => { wsRef.current = null; };
    return () => ws.close();
  }, [activeConv]);

  const loadMessages = useCallback(async (conv) => {
    setActiveConv(conv);
    try {
      const d = await chatApi.getMessages(conv.conversation_id);
      setMessages(d.messages || []);
      setConversations(prev => prev.map(c => c.conversation_id === conv.conversation_id ? { ...c, unread_count: 0 } : c));
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { toast.error('Failed to load messages'); }
  }, []);

  const sendMessage = async () => {
    if (!msgInput.trim() || !activeConv) return;
    const content = msgInput.trim();
    setMsgInput('');
    try {
      const msg = await chatApi.sendMessage(activeConv.conversation_id, { content });
      setMessages(prev => [...prev, msg]);
      setConversations(prev => prev.map(c => c.conversation_id === activeConv.conversation_id ? { ...c, last_message: content, last_message_at: msg.created_at } : c));
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { toast.error('Failed to send'); }
  };

  const searchUsers = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const d = await chatApi.searchUsers(q);
      setSearchResults(d.users || []);
    } catch {}
  };

  const startDM = async (targetUser) => {
    try {
      const conv = await chatApi.createConversation({ type: 'dm', participants: [user.user_id, targetUser.user_id] });
      setShowNew(false);
      setSearchQuery('');
      setSearchResults([]);
      const exists = conversations.find(c => c.conversation_id === conv.conversation_id);
      if (!exists) setConversations(prev => [conv, ...prev]);
      loadMessages(conv);
    } catch (e) { toast.error(e.message); }
  };

  const getConvName = (conv) => {
    if (conv.type === 'group') return conv.name || 'Group Chat';
    const otherIds = (conv.participants || []).filter(p => p !== user?.user_id);
    const names = conv.participant_names || {};
    return otherIds.map(id => names[id] || 'User').join(', ') || 'Chat';
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="chat-page">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/suite')} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <MessageCircle className="w-6 h-6 text-green-500" />
            <h1 className="text-xl font-bold text-foreground">Chat</h1>
          </div>
          <Button onClick={() => setShowNew(true)} data-testid="new-chat-btn"><Plus className="w-4 h-4 mr-1" /> New Chat</Button>
        </div>
      </header>

      <div className="flex-1 flex max-w-6xl mx-auto w-full">
        {/* Conversation List */}
        <div className="w-80 border-r border-border overflow-y-auto shrink-0">
          {loading ? (
            <div className="p-4 space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}</div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center">
              <MessageCircle className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">No conversations yet</p>
            </div>
          ) : (
            conversations.map(conv => (
              <div key={conv.conversation_id} onClick={() => loadMessages(conv)}
                className={`flex items-center gap-3 p-3 cursor-pointer border-b border-border transition-colors hover:bg-muted/50 ${activeConv?.conversation_id === conv.conversation_id ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}
                data-testid={`conv-${conv.conversation_id}`}>
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  {conv.type === 'group' ? <Users className="w-5 h-5 text-primary" /> : <User className="w-5 h-5 text-primary" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-foreground truncate">{getConvName(conv)}</p>
                    <span className="text-[10px] text-muted-foreground shrink-0">{formatTime(conv.last_message_at)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{conv.last_message || 'Start a conversation'}</p>
                </div>
                {conv.unread_count > 0 && (
                  <span className="w-5 h-5 bg-primary text-primary-foreground text-[10px] rounded-full flex items-center justify-center font-bold shrink-0">{conv.unread_count}</span>
                )}
              </div>
            ))
          )}
        </div>

        {/* Messages Area */}
        <div className="flex-1 flex flex-col">
          {activeConv ? (
            <>
              <div className="p-3 border-b border-border bg-card flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  {activeConv.type === 'group' ? <Users className="w-4 h-4 text-primary" /> : <User className="w-4 h-4 text-primary" />}
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{getConvName(activeConv)}</p>
                  {typing && typing.conversation_id === activeConv.conversation_id && (
                    <p className="text-[10px] text-green-500 animate-pulse">typing...</p>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map(msg => (
                  <div key={msg.message_id} className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${msg.sender_id === user?.user_id ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-muted text-foreground rounded-bl-md'}`}>
                      {msg.sender_id !== user?.user_id && <p className="text-[10px] font-semibold mb-0.5 opacity-70">{msg.sender_name}</p>}
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-[9px] mt-1 ${msg.sender_id === user?.user_id ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>{formatTime(msg.created_at)}</p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <div className="p-3 border-t border-border bg-card">
                <div className="flex gap-2">
                  <Input value={msgInput} onChange={e => setMsgInput(e.target.value)} placeholder="Type a message..."
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                    data-testid="message-input" className="flex-1" />
                  <Button onClick={sendMessage} disabled={!msgInput.trim()} data-testid="send-btn"><Send className="w-4 h-4" /></Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto text-muted-foreground/20 mb-3" />
                <p className="text-lg font-semibold text-foreground">Select a conversation</p>
                <p className="text-sm text-muted-foreground">or start a new chat</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Chat Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Chat</DialogTitle></DialogHeader>
          <Input value={searchQuery} onChange={e => searchUsers(e.target.value)} placeholder="Search users by name or email..." data-testid="search-user-input" />
          <div className="max-h-60 overflow-y-auto space-y-1 mt-2">
            {searchResults.map(u => (
              <div key={u.user_id} onClick={() => startDM(u)}
                className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted cursor-pointer transition-colors" data-testid={`user-${u.user_id}`}>
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{u.name?.[0]?.toUpperCase()}</div>
                <div>
                  <p className="text-sm font-medium text-foreground">{u.name}</p>
                  <p className="text-xs text-muted-foreground">{u.email}</p>
                </div>
              </div>
            ))}
            {searchQuery.length >= 2 && searchResults.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No users found</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
